import { useEffect, useMemo, useRef, useState } from 'react';
import { selectionDraftValid } from '../model/selectionDraftValidation';
import { stripConditions } from '../model/conditionExpression';

const stableStringify = value => {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.keys(value).sort().map(key => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  return JSON.stringify(value);
};
const canonicalSource = source => ({
  id: source.id,
  query: typeof source.query === 'string' ? source.query.trim() : null,
  conditions: stripConditions(source.conditions, ['include_descendants']),
});
const previewable = source => (
  selectionDraftValid({ sources: [source] })
  // The draft can preview the catalog before its first filter is chosen.
  || (source.query === null && source.conditions.match === 'all' && !source.conditions.items.length)
);
const validResponse = (response, sourceId) => (
  response?.source_id === sourceId && Number.isInteger(response.total) && response.total >= 0
  && Array.isArray(response.products) && ['eq', 'estimate'].includes(response.total_relation)
  && typeof response.exact === 'boolean'
  && (response.total_relation === 'eq' ? response.exact : !response.exact)
);

// Bound physical requests across dialogs using the same Redux/API coordinator.
// Cancellation never releases a slot until the actual transport settles.
const pools = new WeakMap();
const poolFor = key => {
  if (!pools.has(key)) pools.set(key, { active: 0, queue: [] });
  return pools.get(key);
};
const drain = pool => {
  while (pool.active < 2 && pool.queue.length) {
    const task = pool.queue.shift();
    if (task.cancelled) continue;
    pool.active += 1;
    Promise.resolve().then(() => !task.cancelled && task.run()).finally(() => {
      pool.active -= 1;
      drain(pool);
    });
  }
};
const cancelEntry = entry => {
  clearTimeout(entry.timer);
  if (entry.task) entry.task.cancelled = true;
  if (entry.controller) entry.controller.abort();
};

export default function useSelectionSourcePreviews({
  sources, languageId, scopeKey, enabled, requestPreview, coordinatorKey,
}) {
  const [snapshot, setSnapshot] = useState({});
  const entries = useRef(new Map());
  const mounted = useRef(true);
  const running = useRef(enabled);
  const request = useRef(requestPreview);
  request.current = requestPreview;
  running.current = enabled;
  const pool = poolFor(coordinatorKey || requestPreview);
  const descriptors = useMemo(() => sources.slice(0, 5).map(item => {
    const source = canonicalSource(item);
    return {
      source, valid: previewable(source),
      fingerprint: stableStringify({ source, languageId: languageId ?? null, scopeKey }),
    };
  }), [sources, languageId, scopeKey]);
  const descriptorKey = descriptors.map(item => item.fingerprint).join('\n');
  const currentKey = useRef('');
  const renderKey = `${enabled}:${descriptorKey}`;
  currentKey.current = renderKey;

  const publish = () => {
    if (!mounted.current) return;
    setSnapshot(Object.fromEntries(Array.from(entries.current, ([id, entry]) => [id, {
      fingerprint: entry.fingerprint, status: entry.status,
      ...(entry.status === 'ready' ? { response: entry.response } : {}),
    }])));
  };
  const enqueue = entry => {
    const stillCurrent = () => mounted.current && running.current
      && entries.current.get(entry.source.id) === entry
      && entry.renderKey === currentKey.current;
    entry.status = 'loading';
    entry.controller = typeof AbortController === 'function' ? new AbortController() : null;
    entry.task = {
      cancelled: false,
      run: async () => {
        if (!stillCurrent()) return;
        try {
          const response = await request.current({
            source: entry.source, language_id: entry.languageId ?? null, page_size: 6,
          }, { signal: entry.controller?.signal });
          if (!stillCurrent()) return;
          if (!validResponse(response, entry.source.id)) throw new Error('Invalid selection source preview.');
          entry.response = { ...response, products: response.products.slice(0, 6) };
          entry.status = 'ready';
        } catch (_) {
          if (!stillCurrent()) return;
          entry.status = 'error';
        }
        publish();
      },
    };
    pool.queue.push(entry.task);
    publish();
    drain(pool);
  };

  useEffect(() => {
    const previous = entries.current;
    const next = new Map();
    if (enabled) descriptors.forEach(descriptor => {
      const existing = previous.get(descriptor.source.id);
      if (existing?.fingerprint === descriptor.fingerprint) {
        existing.renderKey = renderKey;
        next.set(descriptor.source.id, existing);
        return;
      }
      const entry = { ...descriptor, languageId, renderKey, status: descriptor.valid ? 'validating' : 'invalid' };
      next.set(descriptor.source.id, entry);
      if (descriptor.valid) entry.timer = setTimeout(() => {
        if (entries.current.get(entry.source.id) === entry && running.current) enqueue(entry);
      }, 350);
    });
    // Swap first so a synchronous abort cannot publish an old result.
    entries.current = next;
    previous.forEach((entry, id) => {
      if (next.get(id) !== entry) cancelEntry(entry);
    });
    publish();
    drain(pool);
  }, [descriptorKey, enabled]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      entries.current.forEach(cancelEntry);
      entries.current.clear();
    };
  }, []);

  const previews = Object.fromEntries(descriptors.map(descriptor => {
    const cached = snapshot[descriptor.source.id];
    return [descriptor.source.id, enabled && cached?.fingerprint === descriptor.fingerprint
      ? cached : { status: descriptor.valid ? 'validating' : 'invalid' }];
  }));
  const retry = id => {
    const entry = entries.current.get(id);
    if (entry?.status === 'error' && running.current) enqueue(entry);
  };
  return { previews, retry };
}
